import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Form, Row, Col } from "react-bootstrap";
// import Divider from "theme/components/common/Divider";
// import SocialAuthButtons from "theme/components/authentication/SocialAuthButtons";
import { useFinishLogin, useLogin } from "../../state/auth";
import { gettext } from "../../i18n";
import MfaCodeForm from "./mfa/MfaCodeForm";
import {
  MfaLoginChallenge,
  VerificationMethod,
  requestLoginEmailCode,
  verifyLogin,
} from "../../utils/mfa";

interface LoginFormProps {
  layout: string;
  hasLabel: boolean;
}

const LoginForm = ({ hasLabel = false, layout = "simple" }: LoginFormProps) => {
  const login = useLogin();
  const finishLogin = useFinishLogin();
  const navigate = useNavigate();
  // Set once the password was right and the login needs a second factor
  const [mfaChallenge, setMfaChallenge] =
    React.useState<MfaLoginChallenge | null>(null);

  // State
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    remember: false,
  });

  // Handler
  const doLogin = async (
    email: string,
    password: string,
    remember?: boolean,
  ) => {
    const { success, error, mfa, user } = await login({
      email,
      password,
      remember,
    });

    if (mfa) {
      setMfaChallenge(mfa);
      return;
    }
    if (!success) {
      toast.error(error || gettext("Login failed"), {
        theme: "colored",
      });
      return;
    }
    onLoggedIn(email, user?.restriction);
  };

  const onLoggedIn = (email: string, restriction?: string | null) => {
    toast.success(gettext("Logged in as %1", email), {
      theme: "colored",
    });
    // A restricted session is shown only what it may do, whatever the path
    navigate(restriction ? "/" : "/stations");
  };

  // The second step of a login with MFA; resolves to an error message, if any
  const verifySecondFactor = async (
    method: VerificationMethod,
    code?: string,
  ): Promise<string | null> => {
    if (!mfaChallenge) {
      return null;
    }
    let response;
    try {
      response = await verifyLogin(mfaChallenge.challenge, method, code);
    } catch (e) {
      const message = gettext((e as Error).message);
      if ((e as Error).message?.includes("log in again")) {
        // The login has expired or had too many wrong codes: start over
        setMfaChallenge(null);
        toast.error(message, { theme: "colored" });
      }
      return message;
    }
    const { success, error, user } = await finishLogin(response);
    if (!success) {
      return error || gettext("Login failed");
    }
    onLoggedIn(formData.email, user?.restriction);
    return null;
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    doLogin(formData.email, formData.password, formData.remember).catch((e) => {
      console.error("Error in login:", e);
    });
  };

  // const handleSocialAuth = (e: any) => {
  //   e.preventDefault();
  //   doLogin("foo@example.invalid", "password", formData.remember).catch((e) => {
  //     console.error("Error in login:", e);
  //   });
  // };

  const handleFieldChange = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (mfaChallenge) {
    return (
      <div className="LoginMfaStep">
        <p>
          <strong>{gettext("Confirm your login")}</strong>
        </p>
        <MfaCodeForm
          methods={mfaChallenge.methods}
          preferred={mfaChallenge.preferred}
          emailHint={mfaChallenge.email_hint}
          emailSent={mfaChallenge.email_sent}
          onRequestEmail={async () => {
            await requestLoginEmailCode(mfaChallenge.challenge);
          }}
          onVerify={verifySecondFactor}
          submitLabel={gettext("Log in")}
        />
        <hr />
        <Button
          variant="link"
          size="sm"
          className="p-0"
          onClick={() => setMfaChallenge(null)}
        >
          {gettext("Back")}
        </Button>
        <p className="fs--1 text-muted mt-2 mb-0">
          {gettext(
            "Lost your phone or passkey and your backup codes? Contact your organization's administrator or support@mittausguru.fi.",
          )}
        </p>
      </div>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Email address</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? gettext("Email address") : ""}
          value={formData.email}
          name="email"
          autoComplete="username"
          onChange={handleFieldChange}
          type="email"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Password</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? gettext("Password") : ""}
          value={formData.password}
          name="password"
          autoComplete="current-password"
          onChange={handleFieldChange}
          type="password"
        />
      </Form.Group>

      <Row className="justify-content-between align-items-center">
        <Col xs="auto">
          <Form.Check type="checkbox" id="rememberMe">
            <Form.Check.Input
              type="checkbox"
              name="remember"
              checked={formData.remember}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  remember: e.target.checked,
                })
              }
            />
            <Form.Check.Label className="mb-0">Remember Me</Form.Check.Label>
          </Form.Check>
        </Col>

        <Col xs="auto">
          <Link className="fs--1 mb-0" to="/authentication/forgot-password">
            Forgot Password?
          </Link>
        </Col>
      </Row>

      <Form.Group>
        <Button
          type="submit"
          color="primary"
          className="mt-3 w-100"
          disabled={!formData.email || !formData.password}
        >
          Log in
        </Button>
      </Form.Group>

      {/* <Divider className="mt-4">or log in with</Divider>

      <SocialAuthButtons onClick={handleSocialAuth} /> */}
    </Form>
  );
};

LoginForm.propTypes = {
  layout: PropTypes.string,
  hasLabel: PropTypes.bool,
};

LoginForm.defaultProps = {
  layout: "simple",
  hasLabel: false,
};

export default LoginForm;
